// cartographerOverview — build a single "architecture cartographer"
// sheet from a probed file set.
//
// The output is a compact-format inline sheet (b/cn/g/co shape — the
// same one the per-file documents already use) so the existing
// AtlasSheetCanvas + sheetConverter can render it without any new
// rendering paths. It follows the user-stated rules:
//
//   - One subsystem per BlockGroup (not per sheet — multi-sheet
//     atlases collapse poorly into Free/Advanced tier limits, so we
//     emit a single overview sheet with multiple GROUPS).
//   - Consistent color per domain, re-used across the whole sheet.
//   - 4–7 subsystem groups; tiny buckets merge into "misc".
//   - ~5 blocks per group; folders with >8 files collapse to one
//     block labelled "<folder>/  (N files)".
//   - Layout: groups stacked vertically (one strip per subsystem),
//     blocks laid out left-to-right inside each strip. All
//     coordinates are multiples of 20 per SCHEMA_LAYOUT_RULES.md.
//   - Sheet-link blocks carry a `linkTarget` (docId) pointing at the
//     existing per-file document so the user can drill into the
//     deep-dive sheet.
//
// Pure module — no I/O, no React.

import type { AtlasDocument } from '@/types/atlas';
import {
  CARTOGRAPHER_PALETTE,
  CARTOGRAPHER_ORDER,
} from '@/lib/cartographer/spec';

// ── Palette ──────────────────────────────────────────────────────────
//
// Sourced from cartographer/spec.ts — the single source of truth. We
// only need {color, label} here (icons are picked separately in the
// compact-renderer); kept as a local record for legacy call-sites.

const SUBSYSTEM_PALETTE: Record<string, { color: string; label: string }> =
  Object.fromEntries(
    CARTOGRAPHER_PALETTE.map((r) => [r.key, { color: r.color, label: r.label }]),
  );

const SUBSYSTEM_ORDER: ReadonlyArray<string> = CARTOGRAPHER_ORDER;

// ── Subsystem detection ──────────────────────────────────────────────

/**
 * Classify a single file path into one of the palette buckets. The
 * order of checks matters — more-specific patterns first.
 */
export function classifyFile(filePath: string): keyof typeof SUBSYSTEM_PALETTE {
  const p = filePath.toLowerCase();
  if (/(^|\/)e2e\//.test(p) || /\.(test|spec)\.[a-z]+$/.test(p)) return 'tests';
  if (/^supabase\//.test(p) || /\/edge-functions?\//.test(p)) return 'supabase';
  if (/^scripts?\//.test(p)) return 'scripts';
  if (/^(docs?|documents?|documentation)\//.test(p)) return 'docs';
  if (/^(\.github|\.husky|infra|build|tools|ci|deploy)\//.test(p)) return 'infra';
  if (/^(src\/)?pages?\//.test(p)) return 'pages';
  if (/^(src\/)?contexts?\//.test(p)) return 'contexts';
  if (/^(src\/)?hooks?\//.test(p)) return 'hooks';
  if (/^(src\/)?(ai|aiSkills|aiAgents|llm)\//.test(p) || /^(src\/)?functions\/ai\//.test(p)) return 'ai';
  if (/^(src\/)?(components?|ui|widgets?|gui)\//.test(p)) return 'components';
  if (/^(src\/)?types?\//.test(p)) return 'types';
  if (/^(src\/)?styles?\//.test(p) || /\.css$/.test(p)) return 'styles';
  if (/^(src\/)?(lib|core|utils|services)\//.test(p)) return 'lib';
  return 'misc';
}

// ── Folder-level bucketing ───────────────────────────────────────────

/**
 * Second-level grouping key for a file. We use the second path segment
 * (e.g. `components/admin/Foo.tsx` → `components/admin`) so blocks
 * cluster by feature folder. Files in a top-level folder with no
 * second segment use the top-level itself (e.g. `App.tsx` → `root`).
 */
function folderBucket(filePath: string): string {
  const parts = filePath.split('/');
  if (parts.length >= 3) return `${parts[0]}/${parts[1]}/${parts[2].includes('.') ? '' : parts[2]}`.replace(/\/$/, '');
  if (parts.length >= 2) return `${parts[0]}/${parts[1].includes('.') ? '' : parts[1]}`.replace(/\/$/, '');
  return parts[0] || 'root';
}

// ── Compact-format block / connector types ────────────────────────────

interface CompactBlock {
  id: string;
  t: string;
  co: { x: number; y: number };
  cfg: { n: string; d?: string; linkTarget?: string };
  color?: string;
}

interface CompactGroup {
  id: string;
  n: string;
  color: string;
  ids: string[];
}

// ── Layout constants — all multiples of 20 (SCHEMA_LAYOUT_RULES.md) ─

const GROUP_STRIP_HEIGHT = 240;   // vertical room per subsystem strip
const BLOCK_SPACING_X    = 280;   // horizontal pitch inside a strip
const BLOCK_START_X      = 200;   // left margin so block labels fit
const STRIP_START_Y      = 160;   // top margin for the first strip
const MAX_BLOCKS_PER_GROUP = 8;   // visual cap; folders beyond collapse
const FOLDER_COLLAPSE_THRESHOLD = 6; // ≥6 files → collapse folder bucket

// ── Public entry point ──────────────────────────────────────────────

export interface CartographerInput {
  /** Distinct source-file paths from the probe (sorted or not). */
  files: ReadonlyArray<string>;
  /**
   * documentId-by-filepath, populated from the per-file docs the
   * atlas-builder produced. Used to wire `linkTarget` so each block
   * drills into the deep-dive sheet.
   */
  docByPath: ReadonlyMap<string, AtlasDocument>;
  /** Optional title for the sheet. Defaults to "Architecture Overview". */
  sheetTitle?: string;
}

export interface CartographerSheet {
  /** Compact-format inline payload — feed directly to SheetSummary.inline. */
  inline: unknown;
  /** Stats for telemetry / toast messages. */
  stats: {
    subsystemCount: number;
    groupCount: number;
    blockCount: number;
    collapsedFolderCount: number;
  };
}

/**
 * Produce a single "cartographer" overview sheet. See module docstring
 * for the layout + bucketing rules.
 */
export function buildCartographerSheet(input: CartographerInput): CartographerSheet {
  const { files, docByPath } = input;

  // 1. Bucket files by subsystem.
  const bySubsystem = new Map<string, string[]>();
  for (const f of files) {
    const sub = classifyFile(f);
    if (!bySubsystem.has(sub)) bySubsystem.set(sub, []);
    bySubsystem.get(sub)!.push(f);
  }

  // Drop empty subsystems; merge tiny ones (<3 files) into 'misc'.
  const miscBucket = bySubsystem.get('misc') ?? [];
  for (const [sub, list] of Array.from(bySubsystem.entries())) {
    if (sub === 'misc') continue;
    if (list.length < 3) {
      miscBucket.push(...list);
      bySubsystem.delete(sub);
    }
  }
  if (miscBucket.length > 0) bySubsystem.set('misc', miscBucket);

  // 2. For each subsystem, cluster by folder bucket; collapse big folders.
  const blocks: CompactBlock[] = [];
  const groups: CompactGroup[] = [];
  let collapsedFolderCount = 0;
  let blockCounter = 0;

  const orderedSubs = SUBSYSTEM_ORDER.filter((s) => bySubsystem.has(s));

  orderedSubs.forEach((sub, stripIdx) => {
    const subFiles = bySubsystem.get(sub) ?? [];
    const meta = SUBSYSTEM_PALETTE[sub];
    const stripY = STRIP_START_Y + stripIdx * GROUP_STRIP_HEIGHT;

    // Cluster by folder bucket; collapse buckets with ≥THRESHOLD files
    // into a single block ("folder/  (N files)").
    const byFolder = new Map<string, string[]>();
    for (const f of subFiles) {
      const k = folderBucket(f);
      if (!byFolder.has(k)) byFolder.set(k, []);
      byFolder.get(k)!.push(f);
    }

    const groupBlockIds: string[] = [];
    let col = 0;

    // Stable order: largest folders first so the eye lands on the
    // most-significant clusters.
    const sortedFolders = [...byFolder.entries()].sort((a, b) => b[1].length - a[1].length);

    for (const [folder, folderFiles] of sortedFolders) {
      if (folderFiles.length >= FOLDER_COLLAPSE_THRESHOLD) {
        // Collapsed folder → single block.
        collapsedFolderCount += 1;
        blockCounter += 1;
        const id = `ov_${blockCounter}`;
        // Link to the first file in the folder so drill-down has a target.
        const firstDoc = folderFiles
          .map((p) => docByPath.get(p))
          .find((d): d is AtlasDocument => !!d);
        blocks.push({
          id,
          t: 'sheet-link',
          co: { x: snap20(BLOCK_START_X + col * BLOCK_SPACING_X), y: snap20(stripY) },
          cfg: {
            n: `${folder}/`,
            d: `${folderFiles.length} files`,
            ...(firstDoc ? { linkTarget: firstDoc.id } : {}),
          },
          color: meta.color,
        });
        groupBlockIds.push(id);
        col += 1;
        if (col >= MAX_BLOCKS_PER_GROUP) break;
        continue;
      }

      // Small folder → one block per file (up to remaining cap).
      for (const f of folderFiles) {
        if (col >= MAX_BLOCKS_PER_GROUP) break;
        blockCounter += 1;
        const id = `ov_${blockCounter}`;
        const doc = docByPath.get(f);
        const label = f.split('/').pop() ?? f;
        blocks.push({
          id,
          t: 'sheet-link',
          co: { x: snap20(BLOCK_START_X + col * BLOCK_SPACING_X), y: snap20(stripY) },
          cfg: {
            n: label,
            d: f,
            ...(doc ? { linkTarget: doc.id } : {}),
          },
          color: meta.color,
        });
        groupBlockIds.push(id);
        col += 1;
      }
      if (col >= MAX_BLOCKS_PER_GROUP) break;
    }

    if (groupBlockIds.length === 0) return;

    groups.push({
      id: `og_${sub}`,
      n: `${meta.label} · ${subFiles.length}`,
      color: meta.color,
      ids: groupBlockIds,
    });
  });

  const inline = {
    name: input.sheetTitle ?? 'Architecture Overview',
    co: { x: 0, y: 0 },
    b: blocks,
    cn: [], // No connectors at overview level — groups carry the meaning.
    g: groups,
    __probe_inline: true,
  };

  return {
    inline,
    stats: {
      subsystemCount: orderedSubs.length,
      groupCount: groups.length,
      blockCount: blocks.length,
      collapsedFolderCount,
    },
  };
}

function snap20(v: number): number {
  return Math.round(v / 20) * 20;
}

// Exported for tests.
export const __cartographerInternals = {
  SUBSYSTEM_PALETTE,
  folderBucket,
};
